import { create } from 'zustand';

import { devtools } from 'zustand/middleware';

import * as THREE from 'three';

import useEditorStore from './useEditorStore';
import useFinalizationDataStore from './useFinalizationDataStore';

function disposeOutwardPreviewMesh(previewMesh) {
  if (!previewMesh) return;
  previewMesh.parent?.remove(previewMesh);
  if (previewMesh.geometry?.boundsTree) {
    previewMesh.geometry.disposeBoundsTree();
  }
  previewMesh.geometry?.dispose?.();
  previewMesh.material?.dispose?.();
}

/**

 * Separate, additive store for the new "outward thickness" pipeline.

 *

 * Coexists with `useThicknessStore` (the legacy hollow-shell pipeline) and does

 * not modify any of its state. The new pipeline is opt-in via `enabled`

 * (default false) so the existing thickness flow is byte-identical until a

 * user toggles this on from the new tool card.

 *

 * Workflows wired here:

 *   - Preview: outward normal extrude (workflow #1) - rendered by

 *     `OutwardThicknessHandler` directly on geometry change.

 *   - Apply:   true Manifold minkowski offset (workflow #2 / #4) - executed

 *     via `createOutwardThicknessCommand` and the existing `execCommand` /

 *     `replaceShadedMesh` pipeline.

 */



const DEFAULT_WALL_MM = 3.0;



const useOutwardThicknessStore = create(

  devtools((set, get) => ({

    /** Master switch. When false the new handler is a no-op. */

    enabled: true,



    /** User-controlled wall thickness in millimetres. */

    wallMm: DEFAULT_WALL_MM,



    /**

     * Inner clearance / comfort offset in millimetres. Adds extra outward

     * distance on top of `wallMm` so the visible preview shows a gap band

     * between the original scan surface and the inner socket wall, and the

     * Apply step grows the solid by `wallMm + offsetMm` total.

     */

    offsetMm: 0,



    /** Whether to render the preview overlay. */

    previewVisible: true,



    /** Sphere segments for Manifold ball used in minkowskiSum. Higher = smoother & slower. */

    manifoldSegments: 12,



    /** Optional post-offset simplify tolerance in scene units. 0 disables. */

    manifoldSimplify: 0,



    /** Tangential Taubin smoothing passes applied to the top rim loop. Higher = smoother top edge. */

    rimSmoothing: 16,



    /** Last preview mesh (THREE.Mesh) so the handler can dispose it cleanly. */

    previewMesh: null,

    setPreviewMesh: (m) => set({ previewMesh: m }),



    /** Apply state. Independent from the legacy `useThicknessStore.isThicknessApplied`. */

    isApplied: false,

    setIsApplied: (b) => set({ isApplied: b }),



    /** Latest applied command (for undo button). */

    currentCommand: null,

    setCurrentCommand: (c) => set({ currentCommand: c }),



    /** Running state of the async Manifold worker job. */

    isApplying: false,

    setIsApplying: (b) => set({ isApplying: b }),



    /** Last apply error message, if any. */

    lastError: null,

    setLastError: (m) => set({ lastError: m }),



    // ---- Actions ----

    setEnabled: (enabled) => set({ enabled }),

    setWallMm: (wallMm) => set({ wallMm }),

    setOffsetMm: (offsetMm) => set({ offsetMm }),

    setPreviewVisible: (previewVisible) => set({ previewVisible }),

    setManifoldSegments: (manifoldSegments) => set({ manifoldSegments }),

    setManifoldSimplify: (manifoldSimplify) => set({ manifoldSimplify }),

    setRimSmoothing: (rimSmoothing) => set({ rimSmoothing }),



    /**

     * Create an undoable command that swaps the shaded mesh for the offset result.

     * Caller is expected to compute the new geometry first (via the Manifold worker)

     * and pass it here as `newGeometry`.

     */

    createOutwardThicknessCommand: (newGeometry) => {

      const { sceneRefs, replaceShadedMesh } = useEditorStore.getState();

      const originalMaterial = sceneRefs.material;

      const originalMesh = sceneRefs.shadedMesh;

      if (!newGeometry || !originalMesh) return null;

      // Clone before replaceShadedMesh disposes the live mesh geometry (undo + safety).
      const originalGeometry = originalMesh.geometry.clone();
      const originalMatrix = originalMesh.matrix.clone();
      const originalWallMm = get().wallMm;
      const originalOffsetMm = get().offsetMm;

      return {

        type: 'APPLY_OUTWARD_THICKNESS',

        execute: () => {
          const previewMesh = get().previewMesh;
          disposeOutwardPreviewMesh(previewMesh);

          const newMesh = new THREE.Mesh(newGeometry, originalMaterial);
          newMesh.matrix.copy(originalMatrix);
          newMesh.matrix.decompose(newMesh.position, newMesh.quaternion, newMesh.scale);
          newMesh.matrixAutoUpdate = true;
          newMesh.name = originalMesh.name;
          newMesh.userData.outwardThicknessApplied = true;

          // Compare baseline is left untouched: it must keep showing the original scan.
          replaceShadedMesh(newMesh);

          useFinalizationDataStore.getState().saveThickness(originalWallMm, originalOffsetMm, 'mm');

          set({
            isApplied: true,
            previewMesh: null,
            previewVisible: false,
          });
        },

        undo: () => {
          const restored = new THREE.Mesh(originalGeometry, originalMaterial);
          restored.matrix.copy(originalMatrix);
          restored.matrix.decompose(restored.position, restored.quaternion, restored.scale);
          restored.matrixAutoUpdate = true;
          restored.name = originalMesh.name;
          delete restored.userData.outwardThicknessApplied;

          replaceShadedMesh(restored);

          useFinalizationDataStore.setState((state) => ({
            appliedTools: {
              ...state.appliedTools,
              thickness: { applied: false, value: 0.0, topValue: 0.0, unit: 'mm', appliedAt: null },
            },
          }));

          set({ isApplied: false, wallMm: originalWallMm, offsetMm: originalOffsetMm });
        },

      };

    },



    reset: () =>

      set({

        wallMm: DEFAULT_WALL_MM,

        offsetMm: 0,

        previewVisible: true,

        manifoldSegments: 12,

        manifoldSimplify: 0,

        rimSmoothing: 16,

        previewMesh: null,

        isApplied: false,

        currentCommand: null,

        isApplying: false,

        lastError: null,

      }),

  }))

);



export default useOutwardThicknessStore;

